import { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useForm } from 'react-hook-form'
import { useNavigate, Link } from "react-router-dom";
import ErrorMessages from '../../errorConfig';
import PmsUrlConfig from '../../urlConfig';
import pmsConstants from '../../pmsConstants';
import { emailRegex, eINRegex } from '../common';
import PracticeDetails from './PracticeDetailsComponent';
import AgreementComponent from './AgreementComponent';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/* eslint-disable */

const SignupSchema = Yup.object().shape({
    entityName: Yup.string()
        .required(ErrorMessages.ENTITY_NAME_REQUIRED)
        .min(2, ErrorMessages.NAME_TOO_SHORT)
        .max(50, ErrorMessages.NAME_TOO_LONG),
    password: Yup.string()
        .required(ErrorMessages.PASSWORD_REQUIRED)
        .min(8, ErrorMessages.PASSWORD_LENGTH),
    confPassword: Yup.string()
        .oneOf([Yup.ref('password')], ErrorMessages.PASSWORDS_DONT_MATCH),
    phoneNo: Yup.string()
        .required(ErrorMessages.PHONE_NUM_REQUIRED),
    entityEmail: Yup.string()
        .required(ErrorMessages.EMAIL_REQUIRED)
        .matches(emailRegex, ErrorMessages.EMAIL_INVALID),
    entityEin: Yup.string()
        .required(ErrorMessages.EIN_REQUIRED)
        .matches(eINRegex, ErrorMessages.EIN_INVALID),


    billAddrStreet1: Yup.string().required('Required!'),
    billAddrCity: Yup.string().required('Required!'),
    billAddrState: Yup.string().required('Required!'),
    billAddrZipcode: Yup.string().required('Required!')
        .matches(/^[0-9]+$/, "Must be only digits")
        .min(5, "Must be exactly 5 digits")
        .max(5, "Must be exactly 5 digits"),

    sameAsBilling: Yup.boolean(),

    mailAddrStreet1: Yup.string()
        .when("sameAsBilling", {
            is: false,
            then: Yup.string().required('Required!'),
        }),

    mailAddrCity: Yup.string()
        .when("sameAsBilling", {
            is: false,
            then: Yup.string().required('Required!'),
        }),
    mailAddrState: Yup.string()
        .when("sameAsBilling", {
            is: false,
            then: Yup.string().required('Required!'),
        }),
    mailAddrZipcode: Yup.string()
        .when("sameAsBilling", {
            is: false,
            then: Yup.string().required('Required!')
                .matches(/^[0-9]+$/, "Must be only digits")
                .min(5, "Must be exactly 5 digits")
                .max(5, "Must be exactly 5 digits"),
        }),
})


export default function AddPracticeForm() {
    const { register, setValue, reset } = useForm();
    const navigate = useNavigate();
    // const [serverErr, setServerError] = useState('');
    const [shouldResetForm, setShouldResetForm] = useState(false);
    const [agreementDocumentUrl, setAgreementDocumentUrl] = useState('');
    const [showAgreement, setShowAgreement] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const registrationInProgress = useRef(false);
    
    useEffect(() => {
        if (shouldResetForm) {
            clearInputFields();
            setShouldResetForm(false);
        }
    }, [shouldResetForm]);
    
    const clearInputFields = () => {
        document.getElementById("signup_form").reset();
    }

    const handleAgreementCancel = () => {
        setShowAgreement(false);
        setAgreementDocumentUrl('');
        setPendingRegistration(null);
    };

    const handleAgreementAccept = async (agreement) => {
        if (registrationInProgress.current || !pendingRegistration || !agreement.accepted) return;

        registrationInProgress.current = true;
        setIsRegistering(true);
        setShowAgreement(false);
        const values = pendingRegistration;
        const mailingAddress = values.sameAsBilling ? {
            street1: values.billAddrStreet1,
            street2: values.billAddrStreet2,
            city: values.billAddrCity,
            state: values.billAddrState,
            zip_code: values.billAddrZipcode
        } : {
            street1: values.mailAddrStreet1,
            street2: values.mailAddrStreet2,
            city: values.mailAddrCity,
            state: values.mailAddrState,
            zip_code: values.mailAddrZipcode
        };

        try {
            const response = await Axios.post(PmsUrlConfig.BOS_ENTITY_SIGN_UP_URL, {
                practice_name: values.entityName,
                EIN: values.entityEin,
                practice_email: values.entityEmail,
                phoneNum: values.phoneNo.replace(/[^\d]/g, ''),
                NPI: values.npi,
                website: values.website,
                billing_address: {
                    street1: values.billAddrStreet1,
                    street2: values.billAddrStreet2,
                    city: values.billAddrCity,
                    state: values.billAddrState,
                    zip_code: values.billAddrZipcode
                },
                mailing_address: mailingAddress,
                user: [{
                    firstname: values.entityName,
                    lastname: "admin",
                    username: "",
                    email: values.entityEmail,
                    password: values.password,
                    password_confirm: values.confPassword,
                    user_type: pmsConstants.USER_TYPE_PRACTICE_STRING,
                    active_user: true,
                    super_user: true
                }],
                practice_agreements: [{
                    accepted_date: agreement.acceptedDate,
                    accepted_agreement: agreement.acceptedAgreement
                }]
            });

            if (response.status === 200) {
                const practiceName = response.data.practice_name;
                toast.success(`Practice ${practiceName} registered successfully!`, {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
                setPendingRegistration(null);
                setShouldResetForm(true);
                setTimeout(() => navigate(PmsUrlConfig.LOGIN_URL), 3000);
            } else {
                toast.error(ErrorMessages.SERVER_ERROR, {
                    position: "bottom-center",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                });
            }
        } catch (error) {
            console.log('Error caught:', error);
            console.log('Error response:', error.response);
            console.log('Error status:', error.response?.status);
            console.log('Error data:', error.response?.data);

            const errorMessage = error.response?.data?.error_code?.description || ErrorMessages.SERVER_ERROR;
            toast.error(errorMessage, {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
        } finally {
            registrationInProgress.current = false;
            setIsRegistering(false);
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            <Formik
                initialValues={{
                    entityType: pmsConstants.USER_TYPE_PRACTICE,
                    phoneNo: "",
                    entityName: "",
                    entityEin: "",
                    entityEmail: "",
                    npi: "",
                    website: "",
                    password: "",
                    confPassword: "",
                    sameAsBilling: true,
                    billAddrStreet1: "",
                    billAddrStreet2: "",
                    billAddrCity: "",
                    billAddrState: "",
                    billAddrZipcode: "",
                    mailAddrStreet1: "",
                    mailAddrStreet2: "",
                    mailAddrCity: "",
                    mailAddrState: "",
                    mailAddrZipcode: "",
                }}
                validationSchema={SignupSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const response = await Axios.get(PmsUrlConfig.BOS_GET_PRACTICE_AGREEMENT_URL);
                        const documentUrl = getAgreementDocumentUrl(response.data);
                        if (typeof documentUrl !== 'string' || !isHttpUrl(documentUrl)) {
                            throw new Error('Invalid practice agreement document URL');
                        }

                        setPendingRegistration({ ...values });
                        setAgreementDocumentUrl(documentUrl);
                        setShowAgreement(true);
                    } catch (error) {
                        console.log('Failed to load practice agreement:', error);
                        const errorMessage = error.response?.data?.error_code?.description || 'Unable to load the practice agreement. Please try again.';
                        toast.error(errorMessage, {
                            position: "bottom-center",
                            autoClose: 5000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true
                        });
                    } finally {
                        setSubmitting(false);
                    }
                }}
                onReset={() => {
                    clearInputFields();
                }}
            >
                {({ errors, touched, values, handleChange, setFieldValue, handleBlur, handleReset, isSubmitting, resetForm }) => (
                    <Form id="signup_form" className="space-y-6">
                        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-xl font-semibold text-gray-900">Practice Registration</h3>
                            </div>
                            <div className="p-6">
                                <PracticeDetails
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    handleBlur={handleBlur}
                                    handleChange={handleChange}
                                    setFieldValue={setFieldValue}
                                />
                            </div>
                        </div>

                        <div className="flex space-x-4">
                            <button
                                className="w-32 bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                                type="submit"
                                disabled={isSubmitting || isRegistering || showAgreement}
                            >
                                {isSubmitting ? "Loading agreement..." : isRegistering ? "Registering..." : "Sign Up"}
                            </button>
                            <button
                                className="w-32 bg-gray-100 border border-teal-300 hover:bg-gray-200 text-teal-600 font-bold py-2 px-4 rounded"
                                type="button"
                                onClick={handleReset}
                            >
                                Reset
                            </button>
                        </div>
                        
                        <div className="text-center">
                            <p className="text-gray-600">
                                Already have an account?{' '}
                                <Link 
                                    to={`${PmsUrlConfig.HOME_URL}`} 
                                    className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
                                >
                                    Login
                                </Link>
                            </p>
                        </div>
                    </Form>
                )}
            </Formik>
            {showAgreement && (
                <AgreementComponent
                    agreementType="Practice"
                    documentUrl={agreementDocumentUrl}
                    onAccept={handleAgreementAccept}
                    onCancel={handleAgreementCancel}
                />
            )}
        </div>
    );
}

function isHttpUrl(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

function getAgreementDocumentUrl(responseData) {
    if (typeof responseData === 'string') {
        try {
            responseData = JSON.parse(responseData);
        } catch {
            return responseData;
        }
    }

    return typeof responseData === 'object' && responseData !== null
        ? responseData.url
        : responseData;
}